import get from 'lodash/get';
import capitalize from 'lodash/capitalize';
import toUpper from 'lodash/toUpper';
import toLower from 'lodash/toLower';
import { PROFILE_EDITOR_TYPE } from '@/constants/settings';

// Fills quick response placeholders such as #{first_name} or #{first_name|capitalize} as the side panel does,
// so suggestions read the same. SidePanel still has its own copy and can move to these helpers.

const TRANSFORMATIONS_MAP = new Map([
  ['capitalize', (str) => str.split(' ').map(word => capitalize(word)).join(' ')],
  ['uppercase', toUpper],
  ['lowercase', toLower],
]);

// #{variable} or #{variable|transformation}
const PLACEHOLDER = /#\{([^|}]+)(?:\|([^}]*))?\}/g;

const getProfileValue = (qrVariable, profile) => {
  const type = get(qrVariable, 'type', PROFILE_EDITOR_TYPE.STRING);
  if (type === PROFILE_EDITOR_TYPE.CONCAT_STRING) {
    const strs = [];
    for (const subQrVariable of get(qrVariable, 'fields', [])) {
      strs.push(get(profile, subQrVariable.field, ''));
    }
    return strs.join(' ').trim();
  }
  if (type === PROFILE_EDITOR_TYPE.STRING_LIST) {
    return (get(profile, qrVariable.field, []) || []).join(', ').trim();
  }
  return get(profile, qrVariable.field, '');
};

/**
 * Placeholder values by variable name.
 *
 * @param profilesQrVariables [{ profile, qrVariables }], e.g. the customer's profile with the side
 *   panel's profile mapping, and the agent's profile with AGENT_PROFILE_MAPPING
 */
export const buildQrVariablesValuesMap = (profilesQrVariables) => {
  const resultMap = new Map();
  for (const { profile, qrVariables } of profilesQrVariables) {
    for (const variable of qrVariables || []) {
      const value = getProfileValue(variable, profile);
      if (variable.field) {
        resultMap.set(variable.field, value);
      }
      if (variable.label) {
        resultMap.set(variable.label, value);
      }
      // Enables direct use of fields inside variables of type concat_string, such as first_name.
      if (variable.fields) {
        for (const f of variable.fields) {
          if (f.field) {
            const profileValue = variable.type === PROFILE_EDITOR_TYPE.CONCAT_STRING ? get(profile, f.field, '') : value;
            resultMap.set(f.field, profileValue);
          }
        }
      }
    }
  }
  return resultMap;
};

// Placeholders without a value are left as they are, as in the side panel.
export const fillQrPlaceholders = (content, valuesMap) => (content || '')
  .replaceAll(PLACEHOLDER, (match, variable, transformFn = '') => {
    const name = variable.trim();
    if (!valuesMap.has(name)) {
      return match;
    }
    const value = String(valuesMap.get(name) ?? '');
    const transform = TRANSFORMATIONS_MAP.get(transformFn.trim());
    return transform ? transform(value) : value;
  });
